// src/components/Navbar.jsx
import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../hooks/useAuth.js';
import { PAID_CLOSED } from '../utils/siteMode.js';
import { useCountry } from '../contexts/CountryContext.jsx';

function NavItem({ to, children }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `px-3 py-2 text-sm font-semibold rounded-lg transition ${
          isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-800'
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function CountrySwitcher() {
  const { t } = useTranslation();
  const { country, changeCountry, allCountries } = useCountry();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="px-3 py-2 text-xl"
        aria-label="Change country"
      >
        {allCountries[country].flag}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-100 py-1 min-w-[140px] z-50">
          {Object.entries(allCountries).map(([code, cfg]) => (
            <button
              key={code}
              onClick={() => {
                changeCountry(code);
                setOpen(false);
              }}
              className={`flex items-center gap-2 w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                code === country ? 'font-bold' : ''
              }`}
            >
              <span className="text-lg">{cfg.flag}</span>
              <span>{t(`countries.${code}`)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProfileMenu() {
  const { t } = useTranslation();
  const { profile, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-700"
        aria-label="Profile"
      >
        {(profile?.email || '?').charAt(0).toUpperCase()}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-100 py-1 min-w-[180px] z-50">
          <div className="px-3 py-2 text-xs text-gray-500 truncate border-b">
            {profile?.email}
          </div>
          <Link
            to="/"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-sm hover:bg-gray-50 md:hidden"
          >
            🏠 {t('nav.home')}
          </Link>
          <Link
            to="/dashboard"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-sm hover:bg-gray-50 md:hidden"
          >
            {t('nav.dashboard')}
          </Link>
          <Link
            to="/create"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-sm hover:bg-gray-50 md:hidden"
          >
            {t('nav.create')}
          </Link>
          <Link
            to="/account"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-sm hover:bg-gray-50"
          >
            {t('nav.account')}
          </Link>
          {/* 유료 결제 종료 (2026-08-28, utils/siteMode.js) — 요금제 진입점을 숨긴다. */}
          {!PAID_CLOSED && (
            <Link
              to="/pricing"
              onClick={() => setOpen(false)}
              className="block px-3 py-2 text-sm hover:bg-gray-50"
            >
              {t('nav.pricing')}
            </Link>
          )}
          <button
            onClick={async () => {
              await logout();
              setOpen(false);
              navigate('/');
            }}
            className="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            {t('nav.logout')}
          </button>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { t } = useTranslation();
  const { user, profile } = useAuth();

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-gray-100">
      <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-extrabold">
          <span
            className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-sm"
            style={{ background: 'var(--color-primary)' }}
          >
            CC
          </span>
          <span className="hidden sm:inline">{t('appName')}</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavItem to="/">{t('nav.home')}</NavItem>
          {user && <NavItem to="/dashboard">{t('nav.dashboard')}</NavItem>}
          {user && <NavItem to="/create">{t('nav.create')}</NavItem>}
          {!PAID_CLOSED && <NavItem to="/pricing">{t('nav.pricing')}</NavItem>}
        </nav>

        <div className="flex items-center gap-2">
          {user && (
            <span
              className="badge text-white"
              style={{ background: 'var(--color-primary)' }}
              title="Credits"
            >
              ⚡ {profile?.credits ?? 0}
            </span>
          )}
          <CountrySwitcher />
          {user ? (
            <ProfileMenu />
          ) : (
            <Link to="/login" className="btn-secondary !py-2 !px-3 text-sm">
              {t('auth.login')}
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
